import { useAccountRepository, useFormValues } from '@frontend/repository';
import { SettingClient } from '@frontend/setting/api';
import { SettingKey } from '@frontend/setting/types';
import { CreateUserContact } from '@frontend/user/types';
import { useEffect, useState } from 'react';

import { NotificationPreference } from '../contacts/modal/notification-preferences/notification-preferences.controller';
import { CreateContactModalProps } from './create-contact-modal.component';

export const FORM_ID = 'create-contact-modal-form';

export const useCreateContactModal = (props: CreateContactModalProps) => {
    const { currentAccount } = useAccountRepository();
    const formValues = useFormValues<CreateUserContact>(FORM_ID);
    const [shouldCreateCorporateContact, setShouldCreateCorporateContact] = useState<boolean>(false);
    const [isEmailValid, setIsEmailValid] = useState<boolean>(true);
    const [isMobileValid, setIsMobileValid] = useState<boolean>(true);
    const [accountNotificationSettings, setAccountNotificationSettings] = useState<NotificationPreference | null>(null);
    const [isLoadingSettings, setIsLoadingSettings] = useState<boolean>(true);

    useEffect(() => {
        setIsLoadingSettings(true);
        SettingClient.fetchSettings({ account_id: currentAccount, entity_id: currentAccount })
            .then((res) => {
                const foundNotificationSettings = res.results.find((setting) => setting.key === SettingKey.DEFAULT_CHANNELS);
                if (!foundNotificationSettings) {
                    setAccountNotificationSettings({
                        email_channel: false,
                        mobile_channel: false,
                        push_channel: false,
                        whatsapp_channel: false
                    });
                    return;
                }
                setAccountNotificationSettings({
                    email_channel: (foundNotificationSettings.value as string[]).includes('email'),
                    mobile_channel: (foundNotificationSettings.value as string[]).includes('sms'),
                    push_channel: (foundNotificationSettings.value as string[]).includes('push'),
                    whatsapp_channel: (foundNotificationSettings.value as string[]).includes('whatsapp')
                });
            })
            .finally(() => {
                setIsLoadingSettings(false);
            });
    }, [currentAccount]);

    const getRequiredFields = (): (keyof CreateUserContact)[] => {
        const results: (keyof CreateUserContact)[] = ['language'];

        if (formValues) {
            if (formValues.email_channel) results.push('email');
            if (formValues.mobile_channel) results.push('mobile');
            if (formValues.whatsapp_channel) results.push('mobile');
        }

        return results;
    };

    const checkSubmitDisabled = (values: CreateUserContact): boolean => {
        if (!values.language) return true;
        if (!values.first_name && !values.last_name && !values.mobile && !values.email) return true;
        if (values.email && !isEmailValid) return true;
        if (values.mobile && !isMobileValid) return true;
        if (values.email_channel && !values.email) return true;
        if ((values.mobile_channel || values.whatsapp_channel) && !values.mobile) {
            return true;
        }
        if (!values.push_channel && !values.email_channel && !values.mobile_channel && !values.whatsapp_channel) {
            return true;
        }

        return false;
    };

    return {
        currentAccount,
        shouldCreateCorporateContact,
        setShouldCreateCorporateContact,
        isEmailValid,
        setIsEmailValid,
        isMobileValid,
        setIsMobileValid,
        accountNotificationSettings,
        isLoadingSettings,
        getRequiredFields,
        checkSubmitDisabled
    };
};
